/* eslint-disable @typescript-eslint/no-explicit-any */
import { IPG } from "@/types/summary";
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { isCaretAtStart, placeCaretAtEnd } from "@/utils/range";
import fridayReporter from "@/utils/friday";
import { debounce } from "@common/utils/common";
import recordStore from "@/store/record.store";
import collaborationStore from "@/store/collaboration.store";
import { handlePaste } from "./util";
import { IBlurParaInfo } from "@/types/record";
import "./index.less";

export interface EditItem {
  si: string;
  newContent: string;
}

export interface EditRecordItemRef {
  focus: () => void;
  placeCaretAtEnd: () => void;
  getText: () => string;
}

export interface IDocItemProps {
  record: IPG;
  /** 生成中状态 */
  isGenerating?: boolean;
  /** 占位 */
  placeholder?: string;
  /** 样式 */
  className?: string;
  isPlaying?: boolean;
  underlineOnFocus?: boolean;
  editable?: boolean;
  isTrans?: boolean; // 是否是译文
  hightlight?: (siArr: string[], si: string) => boolean;
  handleClickWordItem?: (bt: number) => void;
  handleDoubleClick?: (pi: string) => void;
  handleEdit: (
    pi: string,
    editItems: EditItem[],
    emptyWordAllowed: boolean
  ) => void;
  handleEnter?: (editItem: EditItem, cIndex: number, pi: string) => void;
  handleMerge?: (pi: string) => void;
  recomputeRowHeights?: () => void;
  onBlur?: (para: IBlurParaInfo) => void;
}

export interface SpeakItemRef {
  focus: () => void;
  placeCaretAtEnd: () => void;
}

const EditRecordItem = forwardRef<EditRecordItemRef, IDocItemProps>(
  (props, ref) => {
    const {
      record,
      className = "",
      editable = true,
      isTrans,
      handleEdit,
      // handleEnter,
      handleMerge,
      recomputeRowHeights,
      onBlur
    } = props;
    const [isEditing, setIsEditing] = useState(false);
    const hasFocus = useRef(false);
    const isComposing = useRef(false);

    const sentNodes = useMemo(() => {
      let html = "";
      // 加多一层过滤，不然如果返回中带有译文的情况下，会多输出一个空白的 span，导致输入的时候光标会自动跳到最后
      record.sc.filter(sItem => isTrans ? sItem.transTc : sItem.tc).forEach((sItem) => {
        html += `<span id="sent" data-real-pi="${sItem.realPara?.pi}" data-parent-id="${sItem.id}" data-sent-bt="${sItem.bt}" data-sent-id="${sItem.si
          }">${isTrans ? sItem.transTc : sItem.tc
          }</span>`;
      });
      return html;
    }, [record.sc]);

    const editItemsRef = useRef({} as Record<string, EditItem>);
    const divRef = useRef<HTMLDivElement>(null);

    // 统一提交
    const handleCommit = (emptyWordAllowed = false) => {
      const editItems: EditItem[] = [];
      for (const key in editItemsRef.current) {
        const editItem = editItemsRef.current[key];
        editItems.push(editItem);
      }
      handleEdit?.(record.pi, editItems, emptyWordAllowed);
    };

    // const handleSplit = () => {
    //   const selection = window.getSelection();
    //   if (selection && selection.rangeCount > 0) {
    //     const range = selection.getRangeAt(0);
    //     const { startContainer, startOffset } = range;

    //     // 获取光标所在的 span 元素
    //     const span: any =
    //       startContainer.nodeType === Node.TEXT_NODE
    //         ? startContainer.parentElement
    //         : startContainer;

    //     const si = span?.dataset.sentId;
    //     const newContent = span?.innerText?.trim();
    //     if (si) {
    //       handleCommit(true);
    //       handleEnter?.({ si, newContent }, startOffset, record.pi);
    //     } else if (isCaretAtEnd(divRef.current)) {
    //       const getLastSpan = (el: any) => {
    //         const spans = el.querySelectorAll("span");
    //         return spans[spans.length - 1];
    //       };
    //       const lastSpan = getLastSpan(divRef.current);
    //       const si = lastSpan?.dataset.sentId;
    //       const newContent = lastSpan?.innerText?.trim();
    //       if (si) {
    //         handleCommit(true);
    //         handleEnter?.({ si, newContent }, newContent.length, record.pi);
    //       } else {
    //         handleCommit(true);
    //         handleEnter?.(
    //           { si: Date.now().toString(), newContent: "" },
    //           0,
    //           record.pi
    //         );
    //       }
    //     }
    //   }
    // };

    // 处理输入事件，合并，拆分
    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === "Backspace") {
        const selection = window.getSelection();
        const selectedText = selection?.toString();
        const hasSelectedText = selectedText && selectedText.length > 0;

        // 如果有选中文本，遵循 del 键默认删除行为
        if (hasSelectedText) return;

        // 如果没有选中文本，且在段落开头，则更新该段修改，并向上合并段落
        if (isCaretAtStart(divRef.current)) {
          handleCommit(true);
          debounceReportEdit();
          handleMerge?.(record.pi);
        }
      }
      if (e.key === "Enter") {
        e.preventDefault();
        debounceReportEdit();
        (e.target as HTMLDivElement).blur();
        // handleSplit();
      }
    };

    const getInitialContent = useCallback(() => {
      editItemsRef.current = {};
      divRef.current?.querySelectorAll("span").forEach((span) => {
        const si = span.dataset.sentId;
        if (si) {
          editItemsRef.current[si] = {
            si: span.dataset.sentId!,
            newContent: span.innerText?.trim(),
          };
        }
      });
    }, []);

    useEffect(() => {
      getInitialContent();
    }, [getInitialContent]);

    // 监听内容变化
    useEffect(() => {
      let observer: MutationObserver;
      if (isEditing) {
        observer = new MutationObserver((mutations) => {
          mutations.forEach((mutation) => {
            if (mutation.type === "characterData") {
              const span = mutation.target.parentElement!;
              const si = span.dataset.sentId;
              if (si) {
                editItemsRef.current[si] = { si, newContent: span!.innerText?.trim() };
              }
              recomputeRowHeights && recomputeRowHeights();
            } else if (mutation.type === "childList") {
              mutation.removedNodes?.forEach((node: any) => {
                if (
                  node.nodeType === Node.ELEMENT_NODE &&
                  node.nodeName?.toLowerCase() === "span"
                ) {
                  const si = node.dataset.sentId;
                  if (si) {
                    editItemsRef.current[si] = { si, newContent: "" };
                  }
                }
              });
              mutation.addedNodes?.forEach((node: any) => {
                if (
                  node.nodeType === Node.ELEMENT_NODE &&
                  node.nodeName?.toLowerCase() === "span"
                ) {
                  const si = node.dataset.sentId;
                  if (si) {
                    editItemsRef.current[si] = {
                      si,
                      newContent: node.innerText?.trim(),
                    };
                  }
                }
                // 粘贴的文本节点
                else if (node.nodeType === Node.TEXT_NODE) {
                  const spanNode = node.parentNode;
                  const si = spanNode?.dataset.sentId;
                  if (si) {
                    editItemsRef.current[si] = {
                      si,
                      newContent: spanNode.innerText?.trim(),
                    };
                  }
                }
              });
              recomputeRowHeights && recomputeRowHeights();
            }
          });
        });
        const config = { characterData: true, childList: true, subtree: true };
        if (divRef.current) {
          observer.observe(divRef.current, config);
          // 监听粘贴事件
          divRef.current.addEventListener("paste", handlePaste);
        }
      }
      return () => {
        observer?.disconnect();
        observer = null as any;
        divRef.current?.removeEventListener("paste", handlePaste);
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isEditing]);

    useImperativeHandle(
      ref,
      () => ({
        focus: () => {
          divRef.current?.focus();
        },
        placeCaretAtEnd: () => {
          const span = divRef.current?.querySelector("span");
          if (span) {
            placeCaretAtEnd(span);
          }
        },
        getText: () => {
          return divRef.current?.innerText || "";
        },
      }),
      []
    );

    const debounceReportEdit = debounce(() => {
      if (location.pathname === "/record") {
        fridayReporter.pushData({
          eventCode: "RecordPage_Edit",
        });
      }
    }, 500);

    // 根据当前页面路径选择使用哪个 store
    const getCurrentStore = () => {
      if (location.pathname.includes('/collaboration') || location.pathname.includes('/simulInter')) {
        return collaborationStore;
      }
      return recordStore;
    };

    const handleInput = (e: any) => {
      console.log('onInput', isComposing.current);
      if (isComposing.current) {
        return;
      }
      debounceReportEdit();
      const input = e.target.innerText;
      const spans = e.target.querySelectorAll("span");
      const spanLength = spans.length;
      const spanNode = spans[spanLength - 1];
      if (spanLength === 0) {
        // 插入 span
        const span = document.createElement("span");
        span.className = "select-text text";
        span.innerText = input?.trim();
        span.dataset.sentId = Date.now().toString();
        e.target.innerText = "";
        e.target.appendChild(span);
        placeCaretAtEnd(span);
      } else if (spanLength >= 1 && spanNode?.innerText === "") {
        spanNode.innerText = input;
        e.target.innerHTML = "";
        e.target.appendChild(spanNode);
        placeCaretAtEnd(spanNode);
      }
    }

    const handleBlur = () => {
      setIsEditing(false);
      handleCommit(false);
      hasFocus.current = false;
      onBlur && onBlur({ ...record, pContent: divRef.current?.innerText || '' });
    }

    return (
      <span
        id="content"
        data-p-id={record.pi}
        className={`outline-none ${className} select-text text w-full inline-block `}
        ref={divRef}
        suppressContentEditableWarning
        contentEditable={editable}
        dangerouslySetInnerHTML={{ __html: sentNodes }}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        onClick={() => {
          setIsEditing(true);
        }}
        onMouseDown={() => {
          setIsEditing(true);
        }}
        onFocus={() => {
          getInitialContent();
          hasFocus.current = true;
          getCurrentStore().getState().setFocusedPara(record);
        }}
        onCompositionStart={() => {
          isComposing.current = true;
        }}
        onCompositionEnd={(e) => {
          isComposing.current = false;
          handleInput(e);
        }}
        onInput={handleInput}
      />
    );
  }
);

export default EditRecordItem;
